import React from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { MessageCircle, Check, Calendar, Users, MapPin } from "lucide-react";
import { LOCATIONS, MESSENGER_URL } from "@/data/staycation";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { n: 1, title: "Tap Book via Messenger", text: "Click the button below to open a chat with Kuya Alexander." },
  { n: 2, title: "Share your dates & unit", text: "Tell us your check-in/check-out dates, preferred location, and number of guests." },
  { n: 3, title: "Get an availability confirmation", text: "We manually check the calendar and reply within minutes during waking hours." },
  { n: 4, title: "Secure your stay", text: "Send the 50% reservation fee, then settle the balance upon check-in. Pack your bags!" },
];

export default function Booking() {
  const [searchParams] = useSearchParams();
  const unitId = searchParams.get("unit");
  const [unit, setUnit] = React.useState(null);

  React.useEffect(() => {
    if (!unitId) return;
    let active = true;
    axios.get(`${API}/units/${unitId}`).then((r) => active && setUnit(r.data)).catch(() => {});
    return () => { active = false; };
  }, [unitId]);

  const messengerLink = unit ? `${MESSENGER_URL}?ref=book_${unit.id}` : MESSENGER_URL;

  return (
    <div data-testid="booking-page">
      <Seo
        title="Book your stay — Chat with us on Messenger"
        description="Booking is quick and personal. Tap Book via Messenger, share your dates and preferred unit, get availability confirmation in minutes, then secure your stay with a 50% reservation fee."
        path="/booking"
      />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pt-14 pb-10">
        <p className="xs-eyebrow">Online Booking</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-2 max-w-2xl">Booking is quick — and personal.</h1>
        <p className="mt-4 text-neutral-600 max-w-2xl">
          We confirm every reservation manually via Facebook Messenger to make sure your dates, unit, and special requests are exactly right. No automated systems, no double-bookings — just real conversations.
        </p>
      </section>

      {/* Date-first search removed by request */}

      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-16 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {STEPS.map((s) => (
            <div key={s.n} className="xs-card p-6 flex gap-5" data-testid={`booking-step-${s.n}`}>
              <div className="shrink-0 h-12 w-12 rounded-2xl bg-[var(--xs-primary-soft)] text-[var(--xs-primary)] inline-flex items-center justify-center font-display text-lg font-black">
                {s.n}
              </div>
              <div>
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-1 text-sm text-neutral-600 leading-relaxed">{s.text}</p>
              </div>
            </div>
          ))}
        </div>

        <aside className="space-y-5">
          {unit ? (
            <div className="xs-card overflow-hidden" data-testid="booking-selected-unit">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={unit.image} alt={unit.name} className="absolute inset-0 h-full w-full object-cover" />
              </div>
              <div className="p-5">
                <p className="text-[11px] uppercase tracking-widest font-semibold text-[var(--xs-primary)]">You selected</p>
                <h3 className="font-display text-lg font-bold mt-1">{unit.name}</h3>
                <p className="text-xs text-neutral-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {unit.near}</p>
                <p className="font-display text-2xl font-bold text-[var(--xs-primary)] mt-3">₱{unit.rate_php.toLocaleString()}<span className="text-sm text-neutral-500 font-normal"> / night</span></p>
              </div>
            </div>
          ) : (
            <div className="xs-card p-6">
              <p className="xs-eyebrow">Choosing a unit</p>
              <p className="text-sm text-neutral-600 mt-3">Haven't picked a unit yet? Browse our locations to find your perfect cozy spot.</p>
              <div className="mt-4 space-y-2.5">
                {LOCATIONS.map((l) => (
                  <a key={l.key} href={`/units?location=${l.key}`} className="flex items-center justify-between rounded-xl border border-neutral-200 px-4 py-2.5 text-sm hover:border-[var(--xs-primary)] transition-colors">
                    <span>{l.name}</span>
                    <span className="text-xs text-neutral-500">{l.area}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="xs-card p-6 bg-[var(--xs-primary)] text-white border-transparent hover:translate-y-0">
            <h3 className="font-display text-xl font-bold">Ready to book?</h3>
            <p className="mt-2 text-sm text-white/90">Tap below to open Messenger and message Kuya Alex.</p>
            <a
              href={messengerLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-messenger-cta"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-[var(--xs-primary)] hover:bg-white/90 px-5 py-3 text-sm font-semibold transition-colors"
            >
              <MessageCircle className="h-4 w-4" /> Book via Messenger
            </a>
            <ul className="mt-5 space-y-1.5 text-xs text-white/90">
              <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 mt-0.5" /> Replies within minutes (working hours)</li>
              <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 mt-0.5" /> No hidden booking fees</li>
              <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 mt-0.5" /> Direct host communication</li>
            </ul>
          </div>

          <div className="xs-card p-6">
            <p className="xs-eyebrow">What to mention</p>
            <ul className="mt-3 space-y-2.5 text-sm text-neutral-700">
              <li className="flex items-start gap-2"><Calendar className="h-4 w-4 mt-0.5 text-[var(--xs-primary)]" /> Check-in & check-out dates</li>
              <li className="flex items-start gap-2"><Users className="h-4 w-4 mt-0.5 text-[var(--xs-primary)]" /> Number of guests</li>
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-[var(--xs-primary)]" /> Preferred location & unit type</li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
